import is from '@sindresorhus/is';
import { useCallback, useMemo, useState } from 'react';

import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import { useJobItemDetails, useJobLevelDetailsQueries } from '../api/use-job-item-details';
import { useJobLevelVersionsQueries } from '../api/use-job-item-versions';
import { useJobLevels } from '../api/use-job-levels';
import type { ComparisonColumn } from '../profile-comparison/ProfileComparisonTable';

export type ComparedLevel = ComparisonColumn & {
    readonly details: JobLevelDetails | undefined;
    readonly level: JobLevel;
};

export type RoleLevelComparisonData = {
    readonly levels: readonly ComparedLevel[];
    readonly role: JobRoleDetails;
};

export type UseRoleLevelComparisonDataResult = {
    readonly data: RoleLevelComparisonData | undefined;
    readonly error: Error | null;
    readonly isLoading: boolean;
    readonly selectLevelVersion: (levelId: string, version: string) => void;
};

export const useRoleLevelComparisonData = (
    architectureId: string,
    roleId: string,
): UseRoleLevelComparisonDataResult => {
    const [selectedVersions, setSelectedVersions] = useState<Readonly<Record<string, string>>>({});
    const roleQuery = useJobItemDetails({
        architectureId,
        itemId: roleId,
        itemType: 'job-role',
        version: undefined,
    });
    const levelsQuery = useJobLevels(architectureId, roleId);
    const levels = levelsQuery.data ?? [];
    const levelDetailQueries = useJobLevelDetailsQueries({
        architectureId,
        levelRequests: levels.map((level) => ({
            itemId: level.id,
            version: selectedVersions[level.id],
        })),
    });
    const levelVersionQueries = useJobLevelVersionsQueries({
        architectureId,
        levelIds: levels.map((level) => level.id),
    });

    const comparedLevels = useMemo(
        (): readonly ComparedLevel[] =>
            levels.map((level, index) => {
                const detailsQuery = levelDetailQueries[index];
                const versionsQuery = levelVersionQueries[index];

                return {
                    details: detailsQuery?.data,
                    error: detailsQuery?.error ?? versionsQuery?.error ?? null,
                    id: level.id,
                    isFetching: detailsQuery?.isFetching === true || versionsQuery?.isFetching === true,
                    isInitialLoading: detailsQuery?.isLoading === true || versionsQuery?.isLoading === true,
                    level,
                    versions: versionsQuery?.data,
                };
            }),
        [levelDetailQueries, levelVersionQueries, levels],
    );

    const role = roleQuery.data?.type === 'job-role' ? roleQuery.data : undefined;
    const data = useMemo<RoleLevelComparisonData | undefined>(() => {
        if (is.undefined(role) || is.undefined(levelsQuery.data)) {
            return undefined;
        }

        return { levels: comparedLevels, role };
    }, [comparedLevels, levelsQuery.data, role]);

    const selectLevelVersion = useCallback((levelId: string, version: string): void => {
        setSelectedVersions((currentVersions) => ({ ...currentVersions, [levelId]: version }));
    }, []);

    return {
        data,
        error: roleQuery.error ?? levelsQuery.error ?? null,
        isLoading: roleQuery.isLoading || levelsQuery.isLoading,
        selectLevelVersion,
    };
};
